import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Plus,
  Trash2,
  Zap,
  CheckCircle,
  AlertTriangle,
  Calendar
} from 'lucide-react';
import { toast } from 'react-toastify';
import { schoolDataApi } from '../../services/api';
import { Student, SchoolClass, FeeStructure, FeeCategory } from '../../types';
import { sortClasses } from '../../utils/classUtils';
import { formatCurrency } from '../../utils/helpers';
import { ConfirmDialog } from '../ui';

type BillType = 'monthly' | 'admission' | 'annual' | 'custom';
type Scope = 'student' | 'class' | 'school';

const billTypeLabels: Record<BillType, string> = {
  monthly: 'Monthly',
  admission: 'Admission',
  annual: 'Annual',
  custom: 'Custom'
};

const monthLabel = (ym: string) => {
  const [year, month] = ym.split('-').map(Number);
  if (!year || !month) return ym;
  return new Date(year, month - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const GenerateBillForm: React.FC = () => {
  const [billType, setBillType] = useState<BillType>('monthly');
  const [scope, setScope] = useState<Scope>('class');

  const [academicYear, setAcademicYear] = useState('');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [dueDate, setDueDate] = useState('');

  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [selectedClass, setSelectedClass] = useState('');

  const [students, setStudents] = useState<Student[]>([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  const [customCategories, setCustomCategories] = useState<FeeCategory[]>([{ category: '', amount: 0 }]);
  const [customDescription, setCustomDescription] = useState('');

  const [previewStructure, setPreviewStructure] = useState<FeeStructure | null | 'none-for-scope'>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number; errors: number } | null>(null);

  useEffect(() => {
    schoolDataApi.classes.getAll().then(data => setClasses(sortClasses(data))).catch(() => setClasses([]));
    schoolDataApi.settings.getAcademicYear().then(setAcademicYear).catch(() => {});
    schoolDataApi.students.getAll().then(setStudents).catch(() => setStudents([]));
  }, []);

  // Look up the matching fee structure to preview amounts for non-custom bills.
  useEffect(() => {
    setResult(null);
    if (billType === 'custom' || !academicYear) {
      setPreviewStructure(null);
      return;
    }

    const targetClass = scope === 'student' ? selectedStudent?.class : scope === 'class' ? selectedClass : undefined;
    if (!targetClass) {
      setPreviewStructure(scope === 'school' ? 'none-for-scope' : null);
      return;
    }

    setLoadingPreview(true);
    schoolDataApi.feeStructures.getByClass(targetClass, academicYear)
      .then(structures => {
        const match = structures.find(fs => fs.type === billType && fs.isActive);
        setPreviewStructure(match || null);
      })
      .catch(() => setPreviewStructure(null))
      .finally(() => setLoadingPreview(false));
  }, [billType, scope, selectedClass, selectedStudent, academicYear]);

  const matchingStudents = useMemo(() => {
    if (studentSearch.trim().length < 2) return [];
    const term = studentSearch.toLowerCase();
    return students
      .filter(s => s.admissionStatus === 'Approved')
      .filter(s => s.name.toLowerCase().includes(term) || s.studentId.toLowerCase().includes(term) || s.parentContact.includes(studentSearch))
      .slice(0, 8);
  }, [students, studentSearch]);

  const classStudentCount = useMemo(
    () => students.filter(s => s.class === selectedClass && s.admissionStatus === 'Approved').length,
    [students, selectedClass]
  );
  const schoolStudentCount = useMemo(() => students.filter(s => s.admissionStatus === 'Approved').length, [students]);

  const customTotal = customCategories.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

  const addCustomCategory = () => setCustomCategories(prev => [...prev, { category: '', amount: 0 }]);
  const removeCustomCategory = (index: number) => setCustomCategories(prev => prev.filter((_, i) => i !== index));
  const updateCustomCategory = (index: number, field: keyof FeeCategory, value: string | number) => {
    setCustomCategories(prev => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  };

  const isReady = () => {
    if (scope === 'student' && !selectedStudent) return false;
    if (scope === 'class' && !selectedClass) return false;
    if (billType === 'custom') {
      return customDescription.trim() && customCategories.every(c => c.category.trim() && c.amount > 0);
    }
    return true;
  };

  const scopeSummary = () => {
    if (scope === 'student') return selectedStudent ? `${selectedStudent.name} (${selectedStudent.class})` : 'a student';
    if (scope === 'class') return selectedClass ? `all students in ${selectedClass} (${classStudentCount} student${classStudentCount === 1 ? '' : 's'})` : 'a class';
    return `all ${schoolStudentCount} students across the school`;
  };

  const handleGenerateClick = () => {
    if (!isReady()) {
      toast.error('Please complete the form before generating');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmGenerate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const feeCategories = billType === 'custom' ? customCategories : undefined;
      const description = billType === 'custom' ? customDescription.trim() : undefined;
      const monthParam = billType === 'monthly' ? month : undefined;

      if (scope === 'student' && selectedStudent) {
        const categories = billType === 'custom' ? customCategories : previewStructure && previewStructure !== 'none-for-scope' ? previewStructure.feeCategories : null;
        if (!categories) {
          toast.error(`No active ${billTypeLabels[billType]} fee structure found for ${selectedStudent.class}`);
          setGenerating(false);
          return;
        }
        const outcome = await schoolDataApi.studentFees.generateBill({
          studentId: selectedStudent.id,
          className: selectedStudent.class,
          billType,
          academicYear,
          month: monthParam,
          feeCategories: categories,
          description,
          dueDate: dueDate || undefined,
          feeStructureId: previewStructure && previewStructure !== 'none-for-scope' ? previewStructure.id : undefined
        });
        setResult(outcome === 'created' ? { created: 1, skipped: 0, errors: 0 } : { created: 0, skipped: 1, errors: 0 });
      } else if (scope === 'class') {
        const summary = await schoolDataApi.studentFees.generateBillsForClass({
          className: selectedClass,
          billType,
          academicYear,
          month: monthParam,
          feeCategories,
          description,
          dueDate: dueDate || undefined
        });
        setResult(summary);
      } else {
        const summary = await schoolDataApi.studentFees.generateBillsForSchool({
          billType,
          academicYear,
          month: monthParam,
          feeCategories,
          description,
          dueDate: dueDate || undefined
        });
        setResult(summary);
      }
      toast.success('Bill generation complete');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate bills');
      console.error('Error generating bills:', error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6 space-y-6">
        {/* Bill type + Scope */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Bill Type</label>
            <select
              value={billType}
              onChange={(e) => setBillType(e.target.value as BillType)}
              className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
            >
              {(['monthly', 'admission', 'annual', 'custom'] as BillType[]).map(type => (
                <option key={type} value={type}>{billTypeLabels[type]}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Generate For</label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as Scope)}
              className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
            >
              <option value="student">Student</option>
              <option value="class">Class</option>
              <option value="school">Whole School</option>
            </select>
          </div>
        </div>

        {/* Scope-specific pickers */}
        {scope === 'student' && (
          <div className="relative">
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Search Student</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7f6e]" />
              <input
                type="text"
                value={selectedStudent ? selectedStudent.name : studentSearch}
                onChange={(e) => {
                  setStudentSearch(e.target.value);
                  setSelectedStudent(null);
                }}
                placeholder="Search by name, student ID, or parent contact..."
                className="w-full pl-10 pr-4 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
              />
            </div>
            {!selectedStudent && matchingStudents.length > 0 && (
              <div className="mt-2 border border-[#3a3122] rounded-lg bg-[#17140f] max-h-56 overflow-y-auto">
                {matchingStudents.map(s => (
                  <div
                    key={s.id}
                    onClick={() => { setSelectedStudent(s); setStudentSearch(''); }}
                    className="p-3 hover:bg-[#28221a] cursor-pointer border-b border-[#3a3122] last:border-b-0"
                  >
                    <p className="text-[#f3ede2] font-medium text-sm">{s.name}</p>
                    <p className="text-[#8a7f6e] text-xs">{s.studentId} &middot; {s.class}{s.section ? ` - ${s.section}` : ''}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {scope === 'class' && (
          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Class</label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
            >
              <option value="">Select Class</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.name}>{cls.name}</option>
              ))}
            </select>
            {selectedClass && (
              <p className="text-xs text-[#8a7f6e] mt-1">{classStudentCount} admitted student{classStudentCount === 1 ? '' : 's'} in this class</p>
            )}
          </div>
        )}

        {scope === 'school' && (
          <p className="text-sm text-[#b9ad9b] bg-[#17140f] border border-[#3a3122] rounded-lg px-4 py-3">
            Will generate for all {schoolStudentCount} admitted students across every class.
          </p>
        )}

        {/* Academic year + month + due date */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Academic Year</label>
            <input
              type="text"
              readOnly
              value={academicYear}
              className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#28221a] text-[#8a7f6e] cursor-not-allowed"
            />
          </div>
          {billType === 'monthly' && (
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Bill Month
              </label>
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Due Date (optional)</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
            />
          </div>
        </div>

        {/* Custom bill categories */}
        {billType === 'custom' && (
          <div className="space-y-3 pt-2 border-t border-[#3a3122]">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">Bill Title / Reason</label>
              <input
                type="text"
                value={customDescription}
                onChange={(e) => setCustomDescription(e.target.value)}
                placeholder="e.g. Sports Day Fee, Field Trip"
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-[#b9ad9b]">Charges</label>
              <button
                type="button"
                onClick={addCustomCategory}
                className="text-xs px-2 py-1 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#28221a] transition-colors flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add Charge
              </button>
            </div>
            <div className="overflow-x-auto border border-[#3a3122] rounded-lg">
              <table className="w-full">
                <thead className="bg-[#28221a]">
                  <tr>
                    <th className="text-left py-2 px-4 text-xs font-medium text-[#8a7f6e] uppercase">Category</th>
                    <th className="text-right py-2 px-4 text-xs font-medium text-[#8a7f6e] uppercase w-40">Amount (₹)</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#3a3122]">
                  {customCategories.map((c, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={c.category}
                          onChange={(e) => updateCustomCategory(i, 'category', e.target.value)}
                          placeholder="e.g. Sports Day Fee"
                          className="bg-transparent text-[#f3ede2] placeholder-[#8a7f6e] w-full px-2 py-1.5 border border-transparent hover:border-[#3a3122] focus:border-[#e2916d] rounded focus:outline-none"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          min={0}
                          value={c.amount}
                          onChange={(e) => updateCustomCategory(i, 'amount', Number(e.target.value))}
                          className="bg-transparent text-[#f3ede2] w-full px-2 py-1.5 border border-transparent hover:border-[#3a3122] focus:border-[#e2916d] rounded focus:outline-none text-right"
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        {customCategories.length > 1 && (
                          <button onClick={() => removeCustomCategory(i)} className="p-1 text-[#8a7f6e] hover:text-[#f38b83] transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[#e2916d] bg-[#28221a]">
                    <td className="px-4 py-2.5 text-[#f3ede2] font-medium">Total</td>
                    <td className="px-4 py-2.5 text-right text-[#e2916d] font-bold">{formatCurrency(customTotal)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Preview for non-custom bills */}
        {billType !== 'custom' && (scope === 'student' || scope === 'class') && (
          <div className="pt-2 border-t border-[#3a3122]">
            {loadingPreview ? (
              <p className="text-sm text-[#8a7f6e]">Looking up fee structure...</p>
            ) : previewStructure && previewStructure !== 'none-for-scope' ? (
              <div className="bg-[#17140f] border border-[#3a3122] rounded-lg p-4">
                <p className="text-xs text-[#8a7f6e] mb-2">
                  Will bill using <span className="text-[#f3ede2] font-mono">{previewStructure.structureId}</span>
                </p>
                <div className="space-y-1">
                  {previewStructure.feeCategories.map((c, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-[#b9ad9b]">{c.category}</span>
                      <span className="text-[#f3ede2]">{formatCurrency(c.amount)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-sm pt-2 mt-2 border-t border-[#3a3122]">
                  <span className="text-[#f3ede2] font-medium">Total per student</span>
                  <span className="text-[#e2916d] font-bold">{formatCurrency(previewStructure.totalAmount)}</span>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-sm text-[#f0b64c] bg-[#332711] rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  No active {billTypeLabels[billType].toLowerCase()} fee structure found
                  {scope === 'class' && selectedClass ? ` for ${selectedClass}` : ''}. Create one in Fee Structures first.
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button
            onClick={handleGenerateClick}
            disabled={generating}
            className="flex items-center space-x-2 px-5 py-2.5 bg-[#e2916d] hover:bg-[#f0a67d] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <Zap className="w-4 h-4" />
            <span>{generating ? 'Generating...' : 'Generate Bill'}</span>
          </button>
        </div>
      </div>

      {result && (
        <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-5 h-5 text-[#4ade80]" />
            <h3 className="text-[#f3ede2] font-semibold">Generation Complete</h3>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-[#8a7f6e]">Bills Created</p>
              <p className="text-xl font-bold text-[#4ade80]">{result.created}</p>
            </div>
            <div>
              <p className="text-xs text-[#8a7f6e]">Already Billed (Skipped)</p>
              <p className="text-xl font-bold text-[#f0b64c]">{result.skipped}</p>
            </div>
            <div>
              <p className="text-xs text-[#8a7f6e]">Errors</p>
              <p className="text-xl font-bold text-[#f38b83]">{result.errors}</p>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmGenerate}
        title="Generate Bill"
        message={`Generate a ${billTypeLabels[billType]} bill${billType === 'monthly' ? ` for ${monthLabel(month)}` : ''} for ${scopeSummary()}?`}
        type="info"
        confirmText="Yes, Generate"
        cancelText="Cancel"
      />
    </div>
  );
};

export default GenerateBillForm;
